/**
 * Staff access tiers for internal-service (COR-476/COR-545), listed from least
 * to most privileged. The order in this tuple IS the hierarchy —
 * `superadmin` ⊃ `admin` ⊃ `support` — so the ranking lives in exactly one place.
 *
 * Source of truth: `services/internal-service/src/services/auth/StaffRole.ts`
 * (the backend enforcement). That module is not importable from this frontend
 * package (`services/internal-service` is a standalone service, not a workspace
 * dependency of `views/internal-service`), so this is a deliberate local mirror.
 * Keep the two tuples in sync; the backend remains the authorization boundary
 * and this copy is advisory (UX only).
 */
export const STAFF_ROLES = ['support', 'admin', 'superadmin'] as const;

export type StaffRole = (typeof STAFF_ROLES)[number];

/**
 * Narrows an unknown value (e.g. a decoded JWT `role` claim) to `StaffRole`.
 * Anything that is not one of the recognised tiers is rejected.
 */
export function isStaffRole(value: unknown): value is StaffRole {
  return typeof value === 'string' && (STAFF_ROLES as readonly string[]).includes(value);
}

/**
 * Hierarchical tier check: a caller "has" the required tier when its position in
 * `STAFF_ROLES` is at least as privileged as the requirement (superadmin ⊇ admin
 * ⊇ support). A caller with no tier meets no bar — fail closed, matching the
 * backend.
 */
export function hasTier(caller: StaffRole | undefined, required: StaffRole): boolean {
  return caller !== undefined && STAFF_ROLES.indexOf(caller) >= STAFF_ROLES.indexOf(required);
}
